import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { Field, propTypes as reduxFormPropTypes } from 'redux-form';
import moment from 'moment';
import { CSSTransitionGroup } from 'react-transition-group';
import CSSTransitionReplace from 'react-css-transition-replace';
import { Checkbox } from 'material-ui';
import keys from 'lodash.keys';
import RFTextField from '../ReduxFormTextField';
import Card from '../CardOld';
import Table from '../Table';
import Button from '../Button';
import Spinner from '../Spinner';
import './index.css';

export const EDIT_CONTENT_ID = 'CHANGE_TIMES_EDIT_CONTENT';
export const DEFAULT_CONTENT_ID = 'CHANGE_TIMES_DEFAULT_CONTENT';
export const EDIT_BTN_ID = 'CHANGE_TIMES_EDIT_BTN';
export const CANCEL_BTN_ID = 'CHANGE_TIMES_CANCEL_BTN';

const propTypes = {
  ...reduxFormPropTypes,
  onEdit: PropTypes.func.isRequired,
  onTimesChange: PropTypes.func.isRequired,
  remindTimeChange: PropTypes.func.isRequired,
  isDisabled: PropTypes.bool,
};

const defaultProps = {
  isDisabled: false,
};

const tableStyle = { borderColor: '#ebebeb' };

const reduceDaysIntoReduxFormFields = (accum, weekDay) => {
  return {
    ...accum,
    [weekDay]: {
      open: fieldProps => (
        <RFTextField
          {...fieldProps}
          showValid={false}
          showInlineValidation={false}
          textFieldProps={{
            currency: true,
            name: fieldProps.input.name,
            placeholder: '00:00',
            mask: '11:11',
            noBorderRadius: true,
            isCentered: true,
          }}
        />
      ),
      close: fieldProps => (
        <RFTextField
          {...fieldProps}
          showValid={false}
          showInlineValidation={false}
          textFieldProps={{
            currency: true,
            name: fieldProps.input.name,
            placeholder: '00:00',
            mask: '11:11',
            noBorderRadius: true,
            isCentered: true,
          }}
        />
      ),
      isOpened: fieldProps => (
        <Checkbox
          checked={fieldProps.input.value}
          onCheck={(event, isInputChecked) => fieldProps.input.onChange(isInputChecked)}
          label={
            <span className="u__capitalize">
              <FormattedMessage id={weekDay} defaultMessage={weekDay} />
            </span>
          }
          labelStyle={fieldProps.labelStyle}
        />
      ),
    },
  };
};

const days = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
  'holiday',
];

const getInputName = (day, type) => `${day}.${type}`;

class ChangeOpeningTimes extends Component {
  constructor(props) {
    super(props);

    this.state = {
      isEditing: false,
      shouldRemind: false,
    };

    const commonCheckboxLabelStyle = {
      textAlign: 'left',
      fontWeight: '300',
      transform: 'translateX(-3px)',
    };

    this.weekDaysLabelStyle = {
      ...commonCheckboxLabelStyle,
      fontSize: '14px',
      width: '120px',
      marginBottom: '0',
    };

    this.remindLabelStyle = {
      ...commonCheckboxLabelStyle,
      lineHeight: '1.3',
      transform: 'translateX(-3px)',
    };

    this.reduxFormFields = days.reduce(reduceDaysIntoReduxFormFields, {});
  }

  componentWillReceiveProps(nextProps) {
    const { dealerId, initialValues } = nextProps;

    if (dealerId !== this.props.dealerId) {
      this.autofillNewValues(initialValues);
    }
  }

  autofillNewValues = values => {
    const { autofill } = this.props;
    const autofillEach = key => autofill(key, values[key]);

    keys(values).forEach(autofillEach);
  };

  editOpeningTimes = () => {
    const { onEdit } = this.props;

    onEdit(true);

    this.setState({ isEditing: true });
  };

  cancelEdit = () => {
    const { reset } = this.props;

    reset();
    this.doneEditing();
  };

  doneEditing = () => {
    const { onEdit } = this.props;

    onEdit(false);

    this.setState({ isEditing: false, isLoading: false });
  };

  changeOpeningTimes = openingTimes => {
    const { shouldRemind } = this.state;
    const { initialValues, onTimesChange, remindTimeChange, isDisabled } = this.props;
    const openingTimesDays = keys(openingTimes);
    const reduceOpenedDays = (accum, weekDay) => {
      const day = openingTimes[weekDay];
      if (day.isOpened) {
        return {
          ...accum,
          [weekDay]: {
            ...day,
            isOpened: undefined, // remove from request
          },
        };
      }

      return {
        ...accum,
        [weekDay]: null,
      };
    };

    const payload = {
      openingTimes: openingTimesDays.reduce(reduceOpenedDays, {}),
    };

    const onSave = () => {
      if (shouldRemind) {
        const today = moment();
        const inOneWeek = today.add(1, 'week');

        remindTimeChange(today.format(), inOneWeek.format());
      }

      this.doneEditing();
    };

    if (!isDisabled) {
      this.setState({ isLoading: true });

      onTimesChange({ openingTimes: initialValues }, payload, 'openingTimes')
        .then(onSave)
        .catch(this.cancelEdit);
    }
  };

  onRemindCheck = (event, isCheked) => {
    this.setState(() => {
      return { shouldRemind: isCheked };
    });
  };

  renderTableHeader = () => {
    const { isEditing } = this.state;
    const renderFirstColumnHeader = () => {
      if (isEditing) {
        return <FormattedMessage id="check_opened_days" defaultMessage="Check the opened days" />;
      }

      return <FormattedMessage id="day" defaultMessage="Day" />;
    };

    return (
      <Table.Header>
        <Table.Row>
          <Table.Cell width="50%">
            <span className="ChangeOpeningTimes__header_text">{renderFirstColumnHeader()}</span>
          </Table.Cell>
          <Table.Cell alignCenter>
            <span className="ChangeOpeningTimes__header_text">
              <FormattedMessage id="opens_at" defaultMessage="Opens at" />
            </span>
          </Table.Cell>
          <Table.Cell alignCenter>
            <span className="ChangeOpeningTimes__header_text">
              <FormattedMessage id="closes_at" defaultMessage="Closes at" />
            </span>
          </Table.Cell>
        </Table.Row>
      </Table.Header>
    );
  };

  renderEditableTimeRows = () => {
    const { currentValues } = this.props;
    const mapTimeRows = weekDay => {
      const dayOpeningTimes = currentValues[weekDay];
      const weekdayCheckBox = (
        <Field
          name={getInputName(weekDay, 'isOpened')}
          component={this.reduxFormFields[weekDay].isOpened}
          labelStyle={this.weekDaysLabelStyle}
        />
      );

      const renderContent = () => {
        if (dayOpeningTimes.isOpened) {
          return (
            <div key="opened-content" className="ChangeOpeningTimes__edition_wrapper">
              <div className="ChangeOpeningTimes__text_field_wrapper">
                <Field
                  name={getInputName(weekDay, 'open')}
                  component={this.reduxFormFields[weekDay].open}
                />
              </div>
              <div className="ChangeOpeningTimes__text_field_wrapper">
                <Field
                  name={getInputName(weekDay, 'close')}
                  component={this.reduxFormFields[weekDay].close}
                />
              </div>
            </div>
          );
        }

        return (
          <div key="closed-content" className="ChangeOpeningTimes__closed_info_wrapper">
            <FormattedMessage id="admin.editOpeningTimes.closed" defaultMessage="Closed" />
          </div>
        );
      };

      const animationName = dayOpeningTimes.isOpened
        ? 'ChangeOpeningTimes-open'
        : 'ChangeOpeningTimes-close';

      return (
        <Table.Row key={weekDay}>
          <Table.Cell noTopBottomPadding>
            <div className="ChangeOpeningTimes__checkbox_weekday_wrapper">{weekdayCheckBox}</div>
          </Table.Cell>
          <Table.Cell colSpan="2" alignCenter noPadding>
            <CSSTransitionGroup
              component="div"
              className="ChangeOpeningTimes__editable_wrapper"
              transitionName={animationName}
              transitionEnterTimeout={325}
              transitionLeaveTimeout={300}
            >
              {renderContent()}
            </CSSTransitionGroup>
          </Table.Cell>
        </Table.Row>
      );
    };

    return days.map(mapTimeRows);
  };

  renderReadOnlyTimeRows = () => {
    const { currentValues } = this.props;
    const mapTimeRows = weekDay => {
      const dayOpeningTimes = currentValues[weekDay];

      if (dayOpeningTimes.isOpened) {
        return (
          <Table.Row key={weekDay}>
            <Table.Cell>
              <span className="u__capitalize">
                <FormattedMessage id={weekDay} defaultMessage={weekDay} />
              </span>
            </Table.Cell>
            <Table.Cell alignCenter>{dayOpeningTimes.open}</Table.Cell>
            <Table.Cell alignCenter>{dayOpeningTimes.close}</Table.Cell>
          </Table.Row>
        );
      }

      return (
        <Table.Row key={weekDay}>
          <Table.Cell>
            <span className="u__capitalize">
              <FormattedMessage id={weekDay} defaultMessage={weekDay} />
            </span>
          </Table.Cell>
          <Table.Cell colSpan="2" alignCenter>
            <FormattedMessage id="admin.editOpeningTimes.closed" defaultMessage="Closed" />
          </Table.Cell>
        </Table.Row>
      );
    };

    return days.map(mapTimeRows);
  };

  renderOpeningTimesContent = () => {
    const { handleSubmit, isDisabled } = this.props;
    const { isEditing } = this.state;

    if (isEditing) {
      return (
        <form
          autoComplete="off"
          key="change-opening-times-edit-content"
          id={EDIT_CONTENT_ID}
          className="ChangeOpeningTimes__card_body_content"
          onSubmit={handleSubmit(this.changeOpeningTimes)}
        >
          <div className="ChangeOpeningTimes__table_wrapper">
            <Table style={tableStyle}>
              {this.renderTableHeader()}
              <Table.Body>{this.renderEditableTimeRows()}</Table.Body>
            </Table>
          </div>
          <div className="ChangeOpeningTimes__errorsWrappers">
            <CSSTransitionGroup
              transitionName="ChangeOpeningTimes-fade"
              transitionEnterTimeout={300}
              transitionLeaveTimeout={225}
            >
              {this.renderValidationErrors()}
            </CSSTransitionGroup>
          </div>
          <div className="ChangeOpeningTimes__btns_wrapper">
            <div className="ChangeOpeningTimes__btn">
              <Button
                id={CANCEL_BTN_ID}
                type="button"
                secondary
                onClick={this.cancelEdit}
                fullWidth
                disabled={isDisabled}
              >
                <FormattedMessage id="general.cancel" defaultMessage="Cancel" />
              </Button>
            </div>
            <div className="ChangeOpeningTimes__btn">
              <Button type="submit" fullWidth>
                <FormattedMessage id="admin.shopinfo.save" defaultMessage="Save" />
              </Button>
            </div>
          </div>
        </form>
      );
    }

    return (
      <div
        key="change-opening-times-default-content"
        id={DEFAULT_CONTENT_ID}
        className="ChangeOpeningTimes__card_body_content"
      >
        <div className="ChangeOpeningTimes__table_wrapper">
          <Table style={tableStyle}>
            {this.renderTableHeader()}
            <Table.Body>{this.renderReadOnlyTimeRows()}</Table.Body>
          </Table>
        </div>
        <div className="ChangeOpeningTimes__btns_wrapper">
          <Button id={EDIT_BTN_ID} onClick={this.editOpeningTimes} fullWidth disabled={isDisabled}>
            <FormattedMessage id="edit" defaultMessage="Edit" />
          </Button>
        </div>
      </div>
    );
  };

  renderValidationErrors = () => {
    const {
      error,
      activeField = '',
      intl: { formatMessage },
    } = this.props;

    if (error && error.length) {
      const mapErrorMessages = err => {
        if (err.type === 'day' && activeField.indexOf(err.weekDay) === -1) {
          const i18nWeekDay = formatMessage({
            id: err.weekDay,
            defaultMessage: err.weekDay,
          });

          return (
            <div key={err.weekDay} className="ChangeOpeningTimes__errorMsg">
              <span className="u__capitalize">{i18nWeekDay}</span>: {err.msg}
            </div>
          );
        }

        if (err.type === 'week') {
          const i18nWeek = formatMessage({
            id: 'week',
            defaultMessage: 'Week',
          });

          return (
            <div key="week-error" className="ChangeOpeningTimes__errorMsg">
              {i18nWeek}: {err.msg}
            </div>
          );
        }

        return null;
      };

      return error.map(mapErrorMessages);
    }

    return null;
  };

  renderLoadingSpinner = () => {
    const { isLoading } = this.state;

    if (isLoading) {
      return (
        <div className="ChangeBottlesPrice__loading_wrapper" key="loading">
          <Spinner fill>
            <FormattedMessage id="saving" defaultMessage="Saving" />
          </Spinner>
        </div>
      );
    }

    return null;
  };

  render() {
    return (
      <Card className="ChangeOpeningTimes">
        <Card.Header>
          <FormattedMessage id="opening_times" defaultMessage="Opening times" />
        </Card.Header>
        <Card.Body>
          <div className="ChangeOpeningTimes__message">
            <FormattedMessage
              id="opening_times.message"
              defaultMessage="Fill this with times that you actually have employees that are able to use Chama Dealer Tool and deliver orders"
            />
          </div>
          <CSSTransitionReplace
            transitionName="CSSTG-fade"
            transitionEnterTimeout={375}
            transitionLeaveTimeout={300}
          >
            {this.renderOpeningTimesContent()}
          </CSSTransitionReplace>
          <CSSTransitionGroup
            transitionName="CSSTG-fade"
            transitionEnterTimeout={375}
            transitionLeaveTimeout={300}
          >
            {this.renderLoadingSpinner()}
          </CSSTransitionGroup>
        </Card.Body>
      </Card>
    );
  }
}

ChangeOpeningTimes.propTypes = propTypes;
ChangeOpeningTimes.defaultProps = defaultProps;

export default ChangeOpeningTimes;



// WEBPACK FOOTER //
// ../ui-components/components/ChangeOpeningTimes/index.jsx